import { FundClassification } from '@/models/common/FundClassification';
import { FundFeatures } from '@/models/common/FundFeatures';
import { FundCoordinatorDtoOut } from '@/models/out/FundCoordinatorDtoOut';
import {
  FundManagementDetailsDtoIn,
  FundManagementSupported,
} from '@/models/in/FundManagementDetailsDtoIn';
import { UpdateWholeFundInfoDtoOut } from '@/models/out/UpdateWholeFundInfoDtoOut';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';

/** Default end date for fund features, mirroring the legacy admin screen. */
export const DEFAULT_FEATURE_END_DATE = '2100-01-01';

export const toDateInputValue = (value?: string | null): string => (value ? value.slice(0, 10) : '');

const today = () => new Date().toISOString().slice(0, 10);

export const emptyCoordinator = (): FundCoordinatorDtoOut => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
});

export const emptySupported = (): FundManagementSupported => ({
  israeliId: '',
  firstName: '',
  lastName: '',
  city: '',
  street: '',
  houseNumber: '',
  email: '',
  phone: '',
});

/**
 * The update endpoint replaces the whole fund, so every dialog sends the full DTO:
 * the section it edited plus the fund's current values for everything else.
 */
export interface FundUpdateOverrides {
  name?: string;
  classification?: FundClassification;
  internalClassification?: string | null;
  coordinators?: FundCoordinatorDtoOut[];
  supporteds?: FundManagementSupported[];
  features?: FundFeatures | null;
}

export const buildUpdateDtoFromFund = (
  fund: FundManagementDetailsDtoIn,
  overrides: FundUpdateOverrides,
  note: string,
): UpdateWholeFundInfoDtoOut => ({
  name: overrides.name ?? fund.name,
  classification: overrides.classification ?? fund.classification,
  internalClassification: 'internalClassification' in overrides
    ? overrides.internalClassification ?? null
    : fund.internalClassification ?? null,
  note,
  coordinators: overrides.coordinators ?? (fund.coordinators ?? []),
  supporteds: overrides.supporteds ?? (fund.supporteds ?? []),
  features: 'features' in overrides ? overrides.features ?? null : fund.features ?? null,
  isNewFrontend: true,
});

/* ---------- Features dialog form ---------- */

export interface FeatureToggleForm {
  enabled: boolean;
  startDate: string;
  endDate: string;
}

export interface FeaturesForm {
  overdraft: FeatureToggleForm & { amount: string; isPositive: boolean };
  fundFreeze: FeatureToggleForm;
  supportRequestsFreeze: FeatureToggleForm;
  reason: string;
}

const emptyToggle = (): FeatureToggleForm => ({
  enabled: false,
  startDate: today(),
  endDate: DEFAULT_FEATURE_END_DATE,
});

export const buildFeaturesForm = (features?: FundFeatures | null): FeaturesForm => ({
  overdraft: features?.overdraft
    ? {
      enabled: true,
      startDate: toDateInputValue(features.overdraft.startDate),
      endDate: toDateInputValue(features.overdraft.endDate),
      amount: String(Math.abs(features.overdraft.amount)),
      isPositive: features.overdraft.amount > 0,
    }
    : { ...emptyToggle(), amount: '', isPositive: false },
  fundFreeze: features?.fundFreeze
    ? {
      enabled: true,
      startDate: toDateInputValue(features.fundFreeze.startDate),
      endDate: toDateInputValue(features.fundFreeze.endDate),
    }
    : emptyToggle(),
  supportRequestsFreeze: features?.supportRequestsFreeze
    ? {
      enabled: true,
      startDate: toDateInputValue(features.supportRequestsFreeze.startDate),
      endDate: toDateInputValue(features.supportRequestsFreeze.endDate),
    }
    : emptyToggle(),
  reason: features?.reason ?? '',
});

const anyFeatureEnabled = (form: FeaturesForm): boolean =>
  form.overdraft.enabled || form.fundFreeze.enabled || form.supportRequestsFreeze.enabled;

/** Returns the i18n key of the first validation error, or null when valid. */
export const validateFeaturesForm = (form: FeaturesForm): string | null => {
  const toggles: FeatureToggleForm[] = [form.overdraft, form.fundFreeze, form.supportRequestsFreeze];
  for (const toggle of toggles) {
    if (!toggle.enabled) continue;
    if (!toggle.startDate || !toggle.endDate) return 'fundsManagement.validation.featureDatesRequired';
    if (toggle.endDate < toggle.startDate) return 'fundsManagement.validation.endDateBeforeStartDate';
  }

  if (form.overdraft.enabled) {
    const amount = Number(form.overdraft.amount);
    if (!form.overdraft.amount || Number.isNaN(amount) || amount === 0) {
      return 'fundsManagement.validation.overdraftAmountRequired';
    }
  }

  if (anyFeatureEnabled(form) && !form.reason.trim()) {
    return 'fundsManagement.validation.featuresReasonRequired';
  }

  return null;
};

export const featuresFormToDto = (form: FeaturesForm): FundFeatures | null => {
  if (!anyFeatureEnabled(form)) return null;

  const absAmount = Math.abs(Number(form.overdraft.amount));
  return {
    overdraft: form.overdraft.enabled
      ? {
        startDate: form.overdraft.startDate,
        endDate: form.overdraft.endDate,
        amount: form.overdraft.isPositive ? absAmount : -absAmount,
      }
      : null,
    fundFreeze: form.fundFreeze.enabled
      ? { startDate: form.fundFreeze.startDate, endDate: form.fundFreeze.endDate }
      : null,
    supportRequestsFreeze: form.supportRequestsFreeze.enabled
      ? { startDate: form.supportRequestsFreeze.startDate, endDate: form.supportRequestsFreeze.endDate }
      : null,
    reason: form.reason.trim(),
  };
};

/* ---------- Per-section validation ---------- */

export const validateCoordinators = (coordinators: FundCoordinatorDtoOut[]): string | null => {
  if (coordinators.length === 0) return 'fundsManagement.validation.coordinatorRequired';
  for (const coordinator of coordinators) {
    if (!coordinator.firstName.trim() || !coordinator.lastName.trim() || !coordinator.email.trim()) {
      return 'fundsManagement.validation.coordinatorFieldsRequired';
    }
  }
  return null;
};

export const validateSupporteds = (supporteds: FundManagementSupported[]): string | null => {
  for (const supported of supporteds) {
    if (!supported.israeliId.trim() || !supported.firstName.trim() || !supported.lastName.trim()) {
      return 'fundsManagement.validation.supportedFieldsRequired';
    }
    if (!isValidIsraeliID(supported.israeliId.trim())) {
      return 'fundsManagement.validation.invalidIsraeliId';
    }
  }
  return null;
};
